import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { History } from 'history';
import { PropagateLoader } from 'react-spinners';
import { get as getDatabase, IDatabaseField, IDatabase, format } from '../../services/database';
import {
    get as getAttachments,
    IAttachment,
    post as postAttachment,
} from '../../services/attachment';
import { save as savePage, get as getPage, IPage, submit as submitPage, approve as approvePage } from 'src/services/pages';
import defaultDatabaseFields from '../../data/db-fields.js';
import { FileSelector } from '../../shared/FileSelector';

import MultiSelect from './MultiSelect';
import Select from './Select';
import Input from './Input';
import Textarea from './Textarea';
import { injectIntl, InjectedIntlProps, defineMessages, FormattedMessage } from 'react-intl';
import { PAGES_PATH } from '../../constants';
import { Map } from './Map';

if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (search, len) {
        if (len === undefined || len > this.length) {
            len = this.length;
        }
        return this.substring(len - search.length, len) === search;
    };
}

export default injectIntl(
    (
        props: InjectedIntlProps & {
            history: History;
            match: { params: { pageName?: string; databaseName?: string } };
        }
    ) => {
        const [database, setDatabase] = useState<IDatabase | null>(null);
        const [rawPage, setRawPage] = useState<IPage | null>(null);
        const [saving, setSaving] = useState<boolean>(false);
        const [sections, setSections] = useState<string[]>([]);
        const [serverAttachments, setServerAttachments] = useState<IAttachment[]>([]);
        const [files, setFiles] = useState<File[]>([]);
        const [selectedServerFileIndicies, setSelectedServerFileIndices] = useState<number[]>([]);
        const { databaseName: isCreate } = props.match.params;

        const messages = defineMessages({
            breadcrumbPages: {
                id: 'pages.create.bc.top',
                defaultMessage: 'Information Pages',
            },
            pageTitleCreate: {
                id: 'pages.title.create',
                defaultMessage: 'Create New Information Page',
            },
            pageTitleUpdate: {
                id: 'pages.title.update',
                defaultMessage: 'Update Information Page',
            },
            missingRequiredField: {
                id: 'pages.missingRequired.error',
                defaultMessage: 'You are missing a value for a required field.',
            },
            fileSelect: {
                id: 'pages.create.attach.select.lbl',
                defaultMessage: 'Upload New Attachment',
            },
        });

        const loadDatabase = async (databaseName: string) => {
            const databaseResult = await getDatabase(databaseName);
            const databaseSections: string[] = [];
            if (databaseResult.success && databaseResult.database) {
                databaseResult.database.fields = databaseResult.database.fields
                    .sort((a, b) => {
                        try {
                            const aMeta = JSON.parse(a.extra);
                            const bMeta = JSON.parse(a.extra);
                            return (
                                (parseInt(`${aMeta.sectionOrder}${aMeta.fieldOrder}`, 10) || 0) -
                                (parseInt(`${bMeta.sectionOrder}${bMeta.fieldOrder}`, 10) || 0)
                            );
                        } catch (ex) {
                            return 0;
                        }
                    })
                    .map((x) => ({
                        ...x,
                        value: undefined,
                        id: `${x.name.replace(' ', '_')}-${Math.random()}`,
                    }));
                databaseResult.database.fields.forEach((x) => {
                    if (databaseSections.indexOf(x.group) === -1) databaseSections.push(x.group);
                });

                setSections(databaseSections);
                setDatabase(databaseResult.database);
            }
        };

        const loadPage = async (pageName: string) => {
            const pageResult = await getPage(pageName);
            const databaseSections: string[] = [];
            if (pageResult.success && pageResult.pages) {
                const page = pageResult.pages[0];
                setRawPage(page);
                const databaseResult = await getDatabase(
                    page.databaseName || (page.database && page.database.name) || ''
                );
                if (databaseResult.success && databaseResult.database) {
                    databaseResult.database.fields = databaseResult.database.fields.map((x) => ({
                        ...x,
                        value: undefined,
                        id: `${x.name.replace(' ', '_')}-${Math.random()}`,
                    }));
                    let db = databaseResult.database;
                    databaseResult.database.fields.forEach((field) => {
                        if (databaseSections.indexOf(field.group) === -1)
                            databaseSections.push(field.group);
                        db = getDatabaseWithUpdatedFieldValue(
                            db,
                            field.id,
                            page.row.map[format(field.group, field.name)]
                                ? JSON.parse(page.row.map[format(field.group, field.name)])
                                : ''
                        );
                    });
                    setSections(databaseSections);
                    setDatabase(db);
                    const attachments = await loadAttachments();
                    if (attachments) matchPageAttachmentsToServerValues(page, attachments);
                }
            }
        };

        const loadAttachments = async (): Promise<IAttachment[] | null> => {
            const attachmentResult = await getAttachments();
            if (attachmentResult.success && Array.isArray(attachmentResult.data)) {
                setServerAttachments(attachmentResult.data);
                return attachmentResult.data;
            }
            return null;
        };

        const matchPageAttachmentsToServerValues = (
            rawPageLocal: IPage,
            serverAttachmentsLocal: IAttachment[]
        ) => {
            if (
                Array.isArray(serverAttachmentsLocal) &&
                rawPageLocal &&
                rawPageLocal.row &&
                rawPageLocal.row.map &&
                Array.isArray(rawPageLocal.row.map.attachments)
            )
                setSelectedServerFileIndices(
                    rawPageLocal.row.map.attachments
                        .map((a: string) => {
                            const i = serverAttachmentsLocal.findIndex((sa) => sa.name === a);
                            if (i !== -1) return i;
                            return null;
                        })
                        .filter((x: null | number) => x !== null)
                );
        };

        const submit = () => {
            save((page: IPage) => {
                if (page?.name && page?.accountName === page?.dbOwnerName)
                    approvePage(page?.name)
                else if (page?.name)
                    submitPage(page?.name)
                else
                    alert('Failed to submit this page. Please reload and try again.')
            })
        }

        const save = async (callback) => {
            try {
                setSaving(true);
                if (database) {
                    const map: any = {};
                    let title = 'TEMP TITLE';
                    let missingField: IDatabaseField | null = null;
                    database.fields.forEach((x: IDatabaseField) => {
                        let extra: any = {}
                        try {
                            extra = JSON.parse(x.extra)
                        } catch (ex) {
                            console.error(ex);
                        }
                        if ((x.group === defaultDatabaseFields[0].name && x.name === 'title') || extra.type === 'title')
                            title = x.value;
                        if (x.required && !x.value) missingField = x;
                        map[format(x.group, x.name)] = JSON.stringify(x.value);
                    });
                    if (title === 'TEMP TITLE')
                        title = database.fields[0].value;
                    if (missingField) {
                        // @ts-ignore
                        alert(
                            `${props.intl.formatMessage(
                                messages.missingRequiredField
                            )} \n\n Section: ${missingField!.group} \n Field: ${missingField!.label
                            }`
                        );
                        setSaving(false);
                        return;
                    }
                    const attachments = selectedServerFileIndicies.map(
                        (i) => serverAttachments[i].name
                    );
                    for (const file of files) {
                        // We need this to load a name so we can save that not the link
                        const fileResult = await postAttachment(file);
                        if (fileResult.success && fileResult.data)
                            attachments.push(fileResult.data.name);
                    }
                    map.attachments = attachments;
                    const { pageName } = props.match.params;
                    console.log({
                        title,
                        account: database.account,
                        name: database.name,
                        map,
                        pageName
                    });
                    const accountName = typeof database?.account === 'string' ? database?.account : database?.account?.name
                    if (!accountName) {
                        return alert('Error saving page.');
                    }
                    const result = await savePage(
                        title,
                        accountName,
                        database.name,
                        { map },
                        pageName
                    );
                    if (result.success) {
                        if (typeof callback === 'function') {
                            await callback?.(result.data)
                        }
                        props.history.push(PAGES_PATH);
                    } else alert('Error saving page.');
                }
            } catch (ex) {
                alert('Error saving page.');
                console.error(ex);
            }
            setSaving(false);
        };

        useEffect(() => {
            const { databaseName, pageName } = props.match.params;
            if (databaseName) {
                loadDatabase(databaseName);
                loadAttachments();
            } else if (pageName) loadPage(pageName);
        }, []);

        const getDatabaseWithUpdatedFieldValue = (
            db: IDatabase,
            id: string,
            value: any,
            add: boolean = true
        ): IDatabase => {
            const dbFieldIndex = db.fields.findIndex((x) => x.id === id);
            if (dbFieldIndex !== -1) {
                if (
                    db.fields[dbFieldIndex].predefined &&
                    db.fields[dbFieldIndex].selection !== 'Single'
                ) {
                    if (!Array.isArray(db.fields[dbFieldIndex].value))
                        db.fields[dbFieldIndex].value = add
                            ? Array.isArray(value)
                                ? value
                                : value === ''
                                    ? []
                                    : [value]
                            : [];
                    else if (add && Array.isArray(value))
                        db.fields[dbFieldIndex].value.push(...value);
                    else if (add) db.fields[dbFieldIndex].value.push(value);
                    else if (!add) {
                        db.fields[dbFieldIndex].value = db.fields[dbFieldIndex].value.filter(
                            (y: string) => y !== value
                        );
                    }
                    db.fields[dbFieldIndex].value = db.fields[dbFieldIndex].value.filter(v => db.fields[dbFieldIndex].options.includes(v))
                } else {
                    db.fields[dbFieldIndex].value = value;
                }
                console.log(db.fields[dbFieldIndex]);
            }
            return db;
        };

        const handleChange = (id: string, value: any, add: boolean = true) => {
            if (database) {
                setDatabase({ ...getDatabaseWithUpdatedFieldValue(database, id, value, add) });
            }
        };

        const renderField = (field: IDatabaseField) => {
            let extra: any = {}
            try {
                extra = JSON.parse(field.extra)
            } catch (er) { }
            if (extra.type === 'map') {
                return (<div className="col-7" key={`${field.name}-${field.id}`}>
                    <Map field={field} onChange={handleChange} />
                </div>)
            } else if (field.predefined && field.selection === 'Single') {
                return (
                    <div className="col-7" key={`${field.name}-${field.id}`}>
                        <Select field={field} onChange={handleChange} />
                    </div>
                );
            } else if (field.predefined) {
                return (
                    <div className="col-7" key={`${field.name}-${field.id}`}>
                        <MultiSelect field={field} onChange={handleChange} />
                    </div>
                );
            } else if (field.type === 'Short Text') {
                return (
                    <div className="col-7" key={`${field.name}-${field.id}`}>
                        <Input field={field} onChange={handleChange} />
                    </div>
                );
            } else {
                return (
                    <div className="col-7" key={`${field.name}-${field.id}`}>
                        <Textarea field={field} onChange={handleChange} />
                    </div>
                );
            }
        };

        const renderSection = (section: string) => {
            if (database) {
                return (
                    <div key={section}>
                        <h3 className="font-weight-light">{section}</h3>
                        {database.fields.map((x) => {
                            if (x.group === section) return renderField(x);
                            return null;
                        })}
                    </div>
                );
            }
            return null;
        };

        const renderSaveButton = () => {
            if (saving) {
                return (
                    <div className="p-5 d-flex justify-content-center">
                        <PropagateLoader size={15} loading={saving} color="#29447b" />
                    </div>
                );
            }
            return (
                <React.Fragment>
                    <button className="primary" onClick={save}>
                        <FormattedMessage id="pages.create.btn.save" defaultMessage="Save" />
                    </button>
                    {/* <button onClick={submit} className="mr-3 primary">
                        <FormattedMessage id="pages.create.btn.submit" defaultMessage="Save & Submit" />
                    </button> */}
                    <Link to={PAGES_PATH}>
                        <FormattedMessage id="pages.create.btn.cancel" defaultMessage="Cancel" />
                    </Link>
                </React.Fragment >
            );
        };

        const handleServerFileSelect = (e: any) => {
            setSelectedServerFileIndices([
                ...selectedServerFileIndicies,
                parseInt(e.currentTarget.value, 10),
            ]);
        };

        const renderFileSelectList = () => {
            if (!serverAttachments.length) return null;
            if (serverAttachments.length === selectedServerFileIndicies.length) return null;
            return (
                <select
                    value=""
                    onChange={handleServerFileSelect}
                    className="form-control custom-select my-3">
                    <FormattedMessage
                        tagName="option"
                        id="pages.create.attach.existing"
                        defaultMessage="Select Existing Attachment"
                    />
                    {serverAttachments.map((at, i) => {
                        if (selectedServerFileIndicies.indexOf(i) !== -1) return null;
                        return (
                            <option key={`file-${i}`} value={i}>
                                {at.fileName}
                            </option>
                        );
                    })}
                </select>
            );
        };

        const handleFileChange = (file: File) => {
            const allowedTypes = ['pdf', 'doc', 'docx', 'jpg', 'bmp', 'tif', 'jpeg', 'png'];
            if (file && file.name) {
                const type = allowedTypes.filter((x) => file.name.toLowerCase().endsWith(x));
                if (type.length) {
                    setFiles([...files, file]);
                    return;
                }
            }
            alert(`Only ${allowedTypes.join(', ')} file types are allowed as attachments.`);
        };

        const renderAttachmentHandler = () => {
            return (
                <React.Fragment>
                    <h3 className="font-weight-light">
                        <FormattedMessage
                            id="pages.create.attach.hdr"
                            defaultMessage="Attachments"
                        />
                    </h3>
                    <p className="small font-weight-lighter">
                        <FormattedMessage
                            id="pages.create.attach.txt"
                            defaultMessage={`Attachments are uploaded files that are linked to your information page.
                        You can upload a new file or a select a file you have already uploaded.
                        Attachments may be linked to multiple information pages, and you may add more than one attachment.`}
                        />
                    </p>
                    <div className="row">
                        <div className="col-md col-sm-12 mt-3">
                            {renderFileSelectList()}
                            <FileSelector
                                clearOnSelect={true}
                                handleFileChange={handleFileChange}
                            />
                        </div>
                        <div className="col-md col-sm-12 mt-3">
                            <h3 className="font-weight-light">
                                <FormattedMessage
                                    id="pages.create.attach.linked"
                                    defaultMessage="Linked Attachments"
                                />
                            </h3>
                            <ul className="pl-0">
                                {files.map((file) => {
                                    return (
                                        <li
                                            key={`selected-f-${file.name}`}
                                            className="d-flex justify-content-between p-2 bd-highlight">
                                            {file.name}
                                            <button
                                                type="button"
                                                className="close"
                                                aria-label="Close"
                                                onClick={() =>
                                                    setFiles(
                                                        files.filter((f) => f.name !== file.name)
                                                    )
                                                }>
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                        </li>
                                    );
                                })}
                                {selectedServerFileIndicies.map((i) => {
                                    return (
                                        <li
                                            key={`selected-file-${serverAttachments[i].name}`}
                                            className="d-flex d-flex justify-content-between p-2 bd-highlight"
                                            onClick={() =>
                                                setSelectedServerFileIndices(
                                                    selectedServerFileIndicies.filter(
                                                        (index) => index !== i
                                                    )
                                                )
                                            }>
                                            {serverAttachments[i].fileName}
                                            <button
                                                type="button"
                                                className="close"
                                                aria-label="Close">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </React.Fragment>
            );
        };

        return (
            <section className="d-flex flex-row flex-fill mar-top-35 p-3 dashboard">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12 breadcrumb-col">
                            <ul>
                                <li>
                                    <Link to={PAGES_PATH}>
                                        {props.intl.formatMessage(messages.breadcrumbPages)}
                                    </Link>
                                </li>
                                <li>
                                    <i className="fas fa-angle-right" />
                                </li>
                                <li>
                                    {props.intl.formatMessage(
                                        isCreate
                                            ? messages.pageTitleCreate
                                            : messages.pageTitleUpdate
                                    )}
                                </li>
                            </ul>
                        </div>
                        <div className="col-lg-12">
                            <div className="sigupInner">
                                <div className="pagetitle">
                                    <h2>
                                        {props.intl.formatMessage(
                                            isCreate
                                                ? messages.pageTitleCreate
                                                : messages.pageTitleUpdate
                                        )}
                                    </h2>
                                    <p>
                                        {isCreate ? (
                                            <FormattedMessage
                                                id="pages.create.txt"
                                                defaultMessage="Please fill out the following form to create a new information page. Fields marked with an asterisk are required to be filled out. Click Submit."
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="pages.edit.txt"
                                                defaultMessage="Please edit the following information to update your information page. Fields marked with an asterisk are required to be filled out. Please click Submit when finished to save your changes."
                                            />
                                        )}
                                    </p>
                                </div>
                                <div className="formContainer">
                                    {sections.map(renderSection)}
                                    {/* {database && database.fields.map(renderField)} */}
                                    {database &&
                                        database.settings.allowAttachments &&
                                        renderAttachmentHandler()}
                                    <div className="formInnerCol">
                                        <div className="formbtn">{renderSaveButton()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
);



// WEBPACK FOOTER //
// ./src/client/pages/Form.tsx